import React, {
  Children,
  type ComponentProps,
  forwardRef,
  type ReactNode,
  useCallback,
} from 'react';
import { LinearGradient } from 'expo-linear-gradient';
import type { ColorValue, ScrollViewProps } from 'react-native';
import { type ScrollView, StyleSheet, View } from 'react-native';
import Animated, {
  useAnimatedRef,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';
import useMergedRef from '@react-hook/merged-ref';

import { useRailItemsRef, useRailNavigationState } from './hooks';
import {
  HorizontalScrollRailFooterText,
  HorizontalScrollRailHeader,
  HorizontalScrollRailItem,
} from './subcomponents';

// ─────────────────────────────────────────────────────────────────────────────

export const HorizontalScrollRail = forwardRef<ScrollView, HorizontalScrollRailProps>(
  (props, ref) => {
    const {
      children: railItems,
      heading = '',
      headingIcon,
      headerPalette = 'dark',
      headerBottomSpacing,
      subtitle = '',
      footerText = '',
      titleStyle,
      headerVariation,
      gap = RAIL_DEFAULT_GAP,
      outerOffset = RAIL_DEFAULT_OUTER_OFFSET,
      showNavControls = true,
      withoutHeaderBorder,
      count = 0,
      countPosition,
      scrollGradientBaseColor = theme.colors.WHITE,
      scrollGradientOverlayEnabled = false,
      onScroll,
      onLayout,
      onContentSizeChange,
      ...scrollViewProps
    } = props;

    const itemsCount = Children.count(railItems);

    // ─── Refs ────────────────────────────────────────────────────────────

    const railRef = useAnimatedRef<Animated.ScrollView>();
    const railItemsRef = useRailItemsRef({ railItemsCount: itemsCount });

    // ─── Shared Values ───────────────────────────────────────────────────

    const railWidthSV = useSharedValue(0);
    const railContentWidthSV = useSharedValue(0);
    const railScrollXSV = useSharedValue(0);

    const leftGradientOpacity = useDerivedValue(() => {
      const hasOverflow = railContentWidthSV.value > railWidthSV.value + SCROLL_EDGE_THRESHOLD;
      const shouldShow = railScrollXSV.value > SCROLL_EDGE_THRESHOLD && hasOverflow;

      return withTiming(shouldShow ? 1 : 0, { duration: theme.transitions.short });
    });

    const rightGradientOpacity = useDerivedValue(() => {
      const hasOverflow = railContentWidthSV.value > railWidthSV.value + SCROLL_EDGE_THRESHOLD;
      const maxScrollX = railContentWidthSV.value - railWidthSV.value;
      const isAtEnd = railScrollXSV.value >= maxScrollX - SCROLL_EDGE_THRESHOLD;
      const shouldShow = hasOverflow && !isAtEnd;

      return withTiming(shouldShow ? 1 : 0, { duration: theme.transitions.short });
    });

    // ─── Navigation ──────────────────────────────────────────────────────

    const {
      scrollToNextItem,
      scrollToPreviousItem,
      setViewableState,
      setViewableStateOnLayout,
      prevItemIndexSV,
      nextItemIndexSV,
    } = useRailNavigationState({ railRef, railItemsRef, outerOffset });

    // ─── Helpers ─────────────────────────────────────────────────────────

    const handleOnLayout = useCallback<OnLayout>(
      (...args) => {
        const [event] = args;

        railWidthSV.value = withTiming(event.nativeEvent.layout.width);

        setViewableStateOnLayout();
        onLayout?.(...args);
      },
      [onLayout, railWidthSV, setViewableStateOnLayout],
    );

    const handleOnContentSizeChange = useCallback<OnContentSizeChange>(
      (...args) => {
        const [width] = args;

        railContentWidthSV.value = width;

        onContentSizeChange?.(...args);
      },
      [onContentSizeChange, railContentWidthSV],
    );

    const handleOnScroll = useCallback<OnScroll>(
      (...args) => {
        const [event] = args;

        railScrollXSV.value = event.nativeEvent.contentOffset.x;
        railContentWidthSV.value = event.nativeEvent.contentSize.width;

        onScroll?.(...args);
        setViewableState();
      },
      [onScroll, railContentWidthSV, railScrollXSV, setViewableState],
    );

    // ─── Styles ──────────────────────────────────────────────────────────

    const contentContainerStyles = useStyle(
      () => ({ paddingHorizontal: Math.abs(outerOffset), columnGap: gap }),
      [outerOffset, gap],
    );
    const railStyle = useStyle(() => ({ marginHorizontal: outerOffset }), [outerOffset]);
    const leftGradientStyles = useStyle(
      () => [styles.gradient, { left: outerOffset }],
      [outerOffset],
    );
    const rightGradientStyles = useStyle(
      () => [styles.gradient, { right: outerOffset }],
      [outerOffset],
    );

    const leftGradientAnimatedStyles = useAnimatedStyle(() => {
      return { opacity: leftGradientOpacity.value };
    });

    const rightGradientAnimatedStyles = useAnimatedStyle(() => {
      return { opacity: rightGradientOpacity.value };
    });

    const gradientColors: ComponentProps<typeof LinearGradient>['colors'] = [
      scrollGradientBaseColor,
      theme.colors.OPACITY.WHITE.TRANSPARENT,
    ];
    const gradientColorsReverse: ComponentProps<typeof LinearGradient>['colors'] = [
      theme.colors.OPACITY.WHITE.TRANSPARENT,
      scrollGradientBaseColor,
    ];

    // ─── Flags ───────────────────────────────────────────────────────────

    const shouldRenderHeader = heading || showNavControls;

    // ─────────────────────────────────────────────────────────────────────

    return (
      <View>
        {shouldRenderHeader ? (
          <HorizontalScrollRailHeader
            heading={heading}
            headingIcon={headingIcon}
            headerPalette={headerPalette}
            headerBottomSpacing={headerBottomSpacing}
            titleStyle={titleStyle}
            subtitle={subtitle}
            headerVariation={headerVariation}
            showNavControls={showNavControls}
            prevItemIndexSV={prevItemIndexSV}
            nextItemIndexSV={nextItemIndexSV}
            withoutHeaderBorder={withoutHeaderBorder}
            countPosition={countPosition}
            count={count}
            onPrevBtnPress={scrollToPreviousItem}
            onNextBtnPress={scrollToNextItem}
          />
        ) : null}

        <View>
          <Animated.ScrollView
            ref={useMergedRef(railRef, ref)}
            style={railStyle}
            contentContainerStyle={contentContainerStyles}
            scrollEventThrottle={16}
            horizontal
            showsHorizontalScrollIndicator={false}
            onContentSizeChange={handleOnContentSizeChange}
            onLayout={handleOnLayout}
            onScroll={handleOnScroll}
            {...scrollViewProps}
          >
            {Children.map(railItems, (item, index) => (
              <HorizontalScrollRailItem key={index} index={index} railItemsRef={railItemsRef}>
                {item}
              </HorizontalScrollRailItem>
            ))}
          </Animated.ScrollView>

          {scrollGradientOverlayEnabled ? (
            <>
              <Animated.View
                pointerEvents="none"
                style={[leftGradientStyles, leftGradientAnimatedStyles]}
              >
                <LinearGradient
                  colors={gradientColors}
                  end={GRADIENT_END}
                  locations={GRADIENT_LOCATIONS}
                  start={GRADIENT_START}
                  style={StyleSheet.absoluteFill}
                />
              </Animated.View>

              <Animated.View
                pointerEvents="none"
                style={[rightGradientStyles, rightGradientAnimatedStyles]}
              >
                <LinearGradient
                  colors={gradientColorsReverse}
                  end={GRADIENT_END}
                  locations={GRADIENT_LOCATIONS}
                  start={GRADIENT_START}
                  style={StyleSheet.absoluteFill}
                />
              </Animated.View>
            </>
          ) : null}
        </View>

        {footerText ? (
          <HorizontalScrollRailFooterText>{footerText}</HorizontalScrollRailFooterText>
        ) : null}
      </View>
    );
  },
);

// ─── Constants ───────────────────────────────────────────────────────────────

const RAIL_DEFAULT_GAP = theme.spacing['4'];
const RAIL_DEFAULT_OUTER_OFFSET = 0;
const SCROLL_EDGE_THRESHOLD = 1;
const GRADIENT_WIDTH = 48;

const GRADIENT_LOCATIONS = [0, 1] as const;
const GRADIENT_START = { x: 0, y: 0 };
const GRADIENT_END = { x: 1, y: 0 };

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  gradient: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: GRADIENT_WIDTH,
    zIndex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HorizontalScrollRailProps = Readonly<{
  children: readonly ReactNode[];
  gap?: number;
  outerOffset?: number;
  footerText?: string;
  count?: number;
  countPosition?: ComponentProps<typeof HorizontalScrollRailHeader>['countPosition']; // prettier-ignore
  showNavControls?: boolean;
  itemVisiblePercentThreshold?: number;
  onContentSizeChange?: (width: number, height: number) => void;
  headerPalette?: 'dark' | 'light';
  shouldResetOffset?: boolean;
  scrollGradientBaseColor?: ColorValue;
  scrollGradientOverlayEnabled?: boolean;
}> &
  Omit<
    ComponentProps<typeof HorizontalScrollRailHeader>,
    | 'headerPalette'
    | 'disablePrevBtn'
    | 'disableNextBtn'
    | 'count'
    | 'countPosition'
    | 'onNextBtnPress'
    | 'onPrevBtnPress'
    | 'prevItemIndexSV'
    | 'nextItemIndexSV'
  > &
  Omit<ScrollViewProps, 'ref' | 'horizontal'>;

type OnLayout = NonNullable<ScrollViewProps['onLayout']>;
type OnScroll = NonNullable<ScrollViewProps['onScroll']>;
type OnContentSizeChange = NonNullable<ScrollViewProps['onContentSizeChange']>;
